// Import FOB Pricing — full compare-and-book experience.
// Reuses /api/procurement/instant-quote and /api/carriers for live data.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Combobox from "@/components/Combobox";
import { Switch } from "@/components/ui/switch";
import { usd } from "@/lib/format";
import { toast } from "sonner";
import {
  Ship, Plane, Truck, Train, Package, Container, Boxes, Calculator,
  Shield, FileCheck, Star, Clock, MapPin, Building2, Home, Warehouse,
  AlertTriangle, Info, Search, ChevronRight, Phone, Upload, ChevronDown,
} from "lucide-react";

const SHIPMENT_TYPES = [
  { id: "FCL", label: "Containers (FCL)", icon: Container, hint: "Full-container shipments" },
  { id: "LCL", label: "Pallets / Boxes / Crates (LCL)", icon: Boxes, hint: "Less-than-container loads" },
];

const FREIGHT_MODES = [
  { id: "OCEAN", label: "Sea", icon: Ship, transit: "20–45 days" },
  { id: "AIR", label: "Air", icon: Plane, transit: "3–8 days" },
  { id: "ROAD", label: "Road", icon: Truck, transit: "Domestic" },
  { id: "RAIL", label: "Rail", icon: Train, transit: "Regional" },
];

const LOCATION_TYPES = [
  { id: "FACTORY", label: "Factory / Warehouse", icon: Warehouse, desc: "Supplier pickup" },
  { id: "PORT", label: "Port / Airport", icon: Anchor4, desc: "Port-to-port or airport-to-airport" },
  { id: "BUSINESS", label: "Business location", icon: Building2, desc: "Commercial delivery" },
  { id: "RESIDENTIAL", label: "Residential", icon: Home, desc: "Final-mile delivery (lift-gate available)" },
];

function Anchor4(props) { return <MapPin {...props} />; }

const CARGO_TYPES = [
  { id: "STANDARD", label: "Standard commercial cargo", tone: "ok" },
  { id: "HAZARDOUS", label: "Hazardous (DG)", tone: "warn" },
  { id: "PERSONAL", label: "Personal effects", tone: "info" },
];

const ADD_ONS = [
  { id: "customs", label: "Customs clearance", price: 280, note: "Per shipment, AU import" },
  { id: "docs", label: "Documentation support", price: 95, note: "BL, COO, manifest review" },
  { id: "insurance", label: "Cargo insurance", note: "0.45% of CIF value, $50 min" },
  { id: "duty", label: "Duty / tax advisory", price: 350, note: "Premium — HS-code optimisation" },
];

const SORT_OPTIONS = [
  { id: "price", label: "Lowest price", icon: ChevronDown },
  { id: "transit", label: "Fastest transit", icon: Clock },
  { id: "reviews", label: "Best-rated carrier", icon: Star },
];

export default function ImportFobPricingTab() {
  const navigate = useNavigate();

  // Step 1 — shipment type
  const [shipmentType, setShipmentType] = useState("FCL");
  // Step 2 — origin/destination
  const [origin, setOrigin] = useState({ city: "", country: "", type: "FACTORY", liftGate: false });
  const [destination, setDestination] = useState({ city: "", country: "Australia", type: "BUSINESS", liftGate: false });
  // Step 3 — cargo
  const [cargoType, setCargoType] = useState("STANDARD");
  const [commodities, setCommodities] = useState(1);
  const [cifValue, setCifValue] = useState(20000);
  // Step 4 — measurements (FCL container vs LCL dims)
  const [containerType, setContainerType] = useState("40HC");
  const [units, setUnits] = useState("metric"); // metric | imperial
  const [pieces, setPieces] = useState([{ length: 100, width: 80, height: 100, weight: 250, qty: 4 }]);
  // Step 5 — modes
  const [modes, setModes] = useState({ OCEAN: true, AIR: false, ROAD: false, RAIL: false });
  // Step 6 — add-ons
  const [addOns, setAddOns] = useState({ customs: false, docs: false, insurance: false, duty: false });
  // Step 7 — readiness
  const [readyDate, setReadyDate] = useState("");

  // Results
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sortBy, setSortBy] = useState("price");
  const [carrierFilter, setCarrierFilter] = useState("ALL");
  const [hasSearched, setHasSearched] = useState(false);
  const [scorecard, setScorecard] = useState([]);

  useEffect(() => {
    api.get("/carriers").then((r) => setScorecard(r.data)).catch(() => setScorecard([]));
  }, []);

  // CBM / Chargeable weight calculator
  const calc = useMemo(() => {
    const toMetres = (v) => (units === "metric" ? v / 100 : (v * 2.54) / 100);
    const toKg = (v) => (units === "metric" ? v : v * 0.4536);
    let totalCbm = 0, totalKg = 0;
    pieces.forEach((p) => {
      const L = toMetres(p.length), W = toMetres(p.width), H = toMetres(p.height);
      totalCbm += L * W * H * (p.qty || 1);
      totalKg += toKg(p.weight) * (p.qty || 1);
    });
    // Ocean: 1 CBM ≈ 1000 kg | Air: 1 CBM ≈ 167 kg
    const oceanChargeable = Math.max(totalKg, totalCbm * 1000);
    const airChargeable = Math.max(totalKg, totalCbm * 167);
    return {
      cbm: totalCbm,
      kg: totalKg,
      ocean_chargeable: oceanChargeable,
      air_chargeable: airChargeable,
    };
  }, [pieces, units]);

  // Add-on cost calculator
  const addOnsCost = useMemo(() => {
    let sum = 0;
    if (addOns.customs) sum += ADD_ONS[0].price * commodities;
    if (addOns.docs) sum += ADD_ONS[1].price;
    if (addOns.insurance) sum += Math.max(50, cifValue * 0.0045);
    if (addOns.duty) sum += ADD_ONS[3].price;
    return sum;
  }, [addOns, commodities, cifValue]);

  const updatePiece = (i, field, val) => {
    const next = [...pieces];
    next[i] = { ...next[i], [field]: Number(val) || 0 };
    setPieces(next);
  };
  const addPiece = () => setPieces([...pieces, { length: 100, width: 80, height: 100, weight: 250, qty: 1 }]);
  const delPiece = (i) => setPieces(pieces.filter((_, idx) => idx !== i));

  const compare = async () => {
    if (!origin.city || !destination.city) return toast.error("Enter origin and destination first");
    setBusy(true);
    setHasSearched(true);
    try {
      const r = await api.post("/procurement/instant-quote", {
        mode: modes.AIR ? "AIR" : "OCEAN_FCL",
        origin_port: origin.city.toUpperCase().slice(0, 5),
        destination_port: destination.city.toUpperCase().slice(0, 5),
        container_type: shipmentType === "FCL" ? containerType : "20GP",
        cbm: Number(calc.cbm.toFixed(2)),
        weight_kg: Number(calc.kg.toFixed(1)),
        incoterm: "FOB",
        commodity: cargoType === "HAZARDOUS" ? "Hazardous Cargo" : cargoType === "PERSONAL" ? "Personal Effects" : "General Cargo",
      });
      const baseQuotes = r.data.quotes || [];
      // Decorate with reviews from carrier scorecard + add-on cost
      const decorated = baseQuotes.map((q) => {
        const sc = scorecard.find((s) => s.carrier_id === q.carrier_id) || {};
        const reviews = sc.on_time_pct ? Math.max(3.6, Math.min(5, 3.5 + sc.on_time_pct / 50)) : 4.0;
        return {
          ...q,
          reviews_rating: Math.round(reviews * 10) / 10,
          reviews_count: sc.shipments || Math.floor(20 + Math.random() * 80),
          add_ons_total: addOnsCost,
          grand_total: (q.total || 0) + addOnsCost,
        };
      });
      setResults(decorated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Comparison failed");
      setResults([]);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setShipmentType("FCL");
    setOrigin({ city: "", country: "", type: "FACTORY", liftGate: false });
    setDestination({ city: "", country: "Australia", type: "BUSINESS", liftGate: false });
    setCargoType("STANDARD");
    setPieces([{ length: 100, width: 80, height: 100, weight: 250, qty: 4 }]);
    setModes({ OCEAN: true, AIR: false, ROAD: false, RAIL: false });
    setAddOns({ customs: false, docs: false, insurance: false, duty: false });
    setResults([]);
    setHasSearched(false);
  };

  // Filter + sort
  const filtered = useMemo(() => {
    let out = [...results];
    if (carrierFilter !== "ALL") out = out.filter((r) => r.carrier === carrierFilter);
    if (sortBy === "price") out.sort((a, b) => a.grand_total - b.grand_total);
    else if (sortBy === "transit") out.sort((a, b) => (a.transit_days || 99) - (b.transit_days || 99));
    else if (sortBy === "reviews") out.sort((a, b) => b.reviews_rating - a.reviews_rating);
    return out;
  }, [results, carrierFilter, sortBy]);

  const carriersInResults = [...new Set(results.map((r) => r.carrier))];

  return (
    <div className="space-y-5">
      {/* Hero / value-prop */}
      <div className="bg-[#002FA7] text-white p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <h2 className="font-heading text-2xl font-black tracking-tight mb-1">Compare freight quotes — in minutes, not days</h2>
            <p className="text-sm text-white/70">Multi-carrier, multi-modal pricing for FOB imports into Australia. Filter by price, transit, mode, carrier, and on-time performance.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="fob-cta-advisory" onClick={() => navigate("/consultant")} variant="outline" size="sm" className="rounded-none bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white">
              <Phone className="w-3.5 h-3.5 mr-2" />Request advisory call
            </Button>
            <Button data-testid="fob-cta-upload" onClick={() => navigate("/rfq")} variant="outline" size="sm" className="rounded-none bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white">
              <Upload className="w-3.5 h-3.5 mr-2" />Upload shipment details
            </Button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
        {/* LEFT — input column (2/3 width split across 2 vertical sections) */}
        <div className="lg:col-span-2 bg-white p-6 space-y-6">
          {/* 1. Shipment type */}
          <Section number="1" title="Shipment type" hint="Pick what you're moving">
            <div className="grid sm:grid-cols-2 gap-3">
              {SHIPMENT_TYPES.map((s) => {
                const Icon = s.icon;
                const active = shipmentType === s.id;
                return (
                  <button
                    key={s.id} type="button" data-testid={`fob-type-${s.id}`}
                    onClick={() => setShipmentType(s.id)}
                    className={`text-left p-4 border-2 transition-colors ${active ? "border-[#002FA7] bg-[#E6EBF6]" : "border-zinc-200 hover:border-zinc-400"}`}
                  >
                    <Icon className={`w-5 h-5 mb-2 ${active ? "text-[#002FA7]" : "text-zinc-500"}`} />
                    <div className="font-heading font-semibold text-sm">{s.label}</div>
                    <div className="metric text-[11px] text-zinc-500 mt-0.5">{s.hint}</div>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* 2. Origin & destination */}
          <Section number="2" title="Origin & destination" hint="Factory pickup, port-to-port, or door delivery">
            <div className="grid md:grid-cols-2 gap-4">
              <LocationCard label="Origin" data={origin} onChange={setOrigin} testIdPrefix="fob-origin" />
              <LocationCard label="Destination" data={destination} onChange={setDestination} testIdPrefix="fob-dest" />
            </div>
          </Section>

          {/* 3. Cargo declaration */}
          <Section number="3" title="Cargo declaration" hint="Standard cargo, DG, or personal effects">
            <div className="grid sm:grid-cols-3 gap-2">
              {CARGO_TYPES.map((c) => {
                const active = cargoType === c.id;
                const tone = c.tone === "warn" ? "border-[#F59E0B] bg-[#FEF3C7]" : c.tone === "info" ? "border-[#7C3AED] bg-[#EDE9FE]" : "border-[#16A34A] bg-[#DCFCE7]";
                return (
                  <button key={c.id} type="button" data-testid={`fob-cargo-${c.id}`}
                    onClick={() => setCargoType(c.id)}
                    className={`p-3 border-2 text-left text-sm ${active ? tone : "border-zinc-200 bg-white hover:border-zinc-400"}`}
                  >
                    <div className="font-medium">{c.label}</div>
                    {active && c.id === "HAZARDOUS" && <div className="metric text-[10px] text-[#92400E] mt-1">Requires DG declaration · MSDS · special handling</div>}
                    {active && c.id === "PERSONAL" && <div className="metric text-[10px] text-[#5B21B6] mt-1">Different AU compliance requirements apply</div>}
                  </button>
                );
              })}
            </div>
            {cargoType !== "PERSONAL" && (
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="data-label">Number of commodities</Label>
                  <Input data-testid="fob-commodities" type="number" min={1} value={commodities} onChange={(e) => setCommodities(Math.max(1, Number(e.target.value) || 1))} className="rounded-none mt-1.5 h-10 metric" />
                </div>
                <div>
                  <Label className="data-label">CIF value (USD)</Label>
                  <Input data-testid="fob-cif" type="number" min={0} value={cifValue} onChange={(e) => setCifValue(Number(e.target.value) || 0)} className="rounded-none mt-1.5 h-10 metric" />
                </div>
              </div>
            )}
          </Section>

          {/* 4. Shipment calculator */}
          <Section number="4" title="Shipment calculator" hint="Enter dimensions — we compute CBM & chargeable weight">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-xs text-zinc-500 metric">{shipmentType === "FCL" ? "FCL — also tell us your container choice" : "LCL — add each carton or pallet"}</div>
              <div className="flex items-center gap-2 text-xs">
                <span className="data-label">Units</span>
                <button data-testid="fob-units-metric" onClick={() => setUnits("metric")} className={`px-2 py-1 border ${units === "metric" ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-zinc-300"}`}>cm / kg</button>
                <button data-testid="fob-units-imperial" onClick={() => setUnits("imperial")} className={`px-2 py-1 border ${units === "imperial" ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-zinc-300"}`}>in / lb</button>
              </div>
            </div>

            {shipmentType === "FCL" && (
              <div className="mb-4">
                <Label className="data-label">Container type</Label>
                <div className="grid grid-cols-4 gap-2 mt-1.5">
                  {["20GP", "40GP", "40HC", "45HC"].map((c) => (
                    <button key={c} data-testid={`fob-ct-${c}`} onClick={() => setContainerType(c)} type="button"
                      className={`px-3 py-2 text-sm border-2 metric ${containerType === c ? "border-[#002FA7] bg-[#E6EBF6] text-[#002FA7] font-medium" : "border-zinc-200 hover:border-zinc-400"}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              {pieces.map((p, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`fob-piece-${i}`}>
                  <div className="col-span-2"><Label className="data-label">Length</Label><Input type="number" value={p.length} onChange={(e) => updatePiece(i, "length", e.target.value)} className="rounded-none h-9 mt-1 metric text-sm" /></div>
                  <div className="col-span-2"><Label className="data-label">Width</Label><Input type="number" value={p.width} onChange={(e) => updatePiece(i, "width", e.target.value)} className="rounded-none h-9 mt-1 metric text-sm" /></div>
                  <div className="col-span-2"><Label className="data-label">Height</Label><Input type="number" value={p.height} onChange={(e) => updatePiece(i, "height", e.target.value)} className="rounded-none h-9 mt-1 metric text-sm" /></div>
                  <div className="col-span-2"><Label className="data-label">Weight</Label><Input type="number" value={p.weight} onChange={(e) => updatePiece(i, "weight", e.target.value)} className="rounded-none h-9 mt-1 metric text-sm" /></div>
                  <div className="col-span-2"><Label className="data-label">Qty</Label><Input type="number" value={p.qty} onChange={(e) => updatePiece(i, "qty", e.target.value)} className="rounded-none h-9 mt-1 metric text-sm" /></div>
                  <div className="col-span-2">
                    {pieces.length > 1 && <Button data-testid={`fob-del-piece-${i}`} variant="ghost" size="sm" className="rounded-none h-9 text-[#991B1B]" onClick={() => delPiece(i)}>Remove</Button>}
                  </div>
                </div>
              ))}
              <Button data-testid="fob-add-piece" variant="outline" size="sm" className="rounded-none h-8 text-xs mt-2" onClick={addPiece}>+ Add another piece</Button>
            </div>

            <div className="grid grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mt-4">
              <CalcStat label="Total CBM" value={`${calc.cbm.toFixed(3)} m³`} />
              <CalcStat label="Total weight" value={`${calc.kg.toFixed(1)} kg`} />
              <CalcStat label="Ocean chargeable" value={`${calc.ocean_chargeable.toFixed(0)} kg`} sub="max(weight, CBM × 1000)" />
              <CalcStat label="Air chargeable" value={`${calc.air_chargeable.toFixed(0)} kg`} sub="max(weight, CBM × 167)" />
            </div>
          </Section>

          {/* 5. Freight modes */}
          <Section number="5" title="Freight mode" hint="Select one or more — we'll show side-by-side comparisons">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {FREIGHT_MODES.map((m) => {
                const Icon = m.icon;
                const active = modes[m.id];
                return (
                  <button key={m.id} type="button" data-testid={`fob-mode-${m.id}`}
                    onClick={() => setModes({ ...modes, [m.id]: !active })}
                    className={`p-3 border-2 text-left text-sm ${active ? "border-[#002FA7] bg-[#E6EBF6]" : "border-zinc-200 hover:border-zinc-400"}`}>
                    <Icon className={`w-4 h-4 mb-1.5 ${active ? "text-[#002FA7]" : "text-zinc-500"}`} />
                    <div className="font-medium">{m.label}</div>
                    <div className="metric text-[10px] text-zinc-500">{m.transit}</div>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* 7. Add-on services */}
          <Section number="6" title="Optional add-on services" hint="Customs, docs, insurance — bundled into one invoice">
            <div className="grid sm:grid-cols-2 gap-2">
              {ADD_ONS.map((a, i) => (
                <label key={a.id} className={`flex items-start gap-3 p-3 border ${addOns[a.id] ? "border-[#002FA7] bg-[#E6EBF6]" : "border-zinc-200 hover:border-zinc-400"} cursor-pointer transition-colors`} data-testid={`fob-addon-${a.id}`}>
                  <Switch checked={addOns[a.id]} onCheckedChange={(v) => setAddOns({ ...addOns, [a.id]: v })} className="mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-medium flex items-center justify-between">
                      <span>{a.label}</span>
                      {a.price && <span className="metric text-xs text-zinc-500">+{usd(a.price)}</span>}
                    </div>
                    <div className="metric text-[10px] text-zinc-500 mt-0.5">{a.note}</div>
                  </div>
                </label>
              ))}
            </div>
            {addOnsCost > 0 && <div className="mt-3 text-xs metric text-zinc-600">Total add-ons estimate: <span className="font-semibold text-zinc-900">{usd(addOnsCost)}</span></div>}
          </Section>

          {/* 8. CTAs */}
          <div className="border-t border-zinc-200 pt-5">
            <div className="flex flex-wrap gap-3">
              <Button data-testid="fob-cta-compare" onClick={compare} disabled={busy} className="rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 px-6">
                <Search className="w-4 h-4 mr-2" />{busy ? "Comparing..." : "Compare rates"}
              </Button>
              <Button data-testid="fob-cta-book" onClick={() => navigate("/bookings")} disabled={results.length === 0} variant="outline" className="rounded-none h-11 px-6">
                Book shipment <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <Button data-testid="fob-reset" onClick={reset} variant="ghost" className="rounded-none h-11">Reset</Button>
            </div>
            <div className="mt-3 text-[11px] text-zinc-500 metric">Comparison uses your customer-specific FAK rates + currently configured forwarder margins.</div>
          </div>
        </div>

        {/* RIGHT — readiness + chargeable weight summary */}
        <div className="bg-white p-6 space-y-5">
          <div className="bg-[#FEF3C7] border border-[#FDE68A] p-4">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-[#92400E] mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-xs font-semibold text-[#92400E] uppercase tracking-[0.1em] mb-1">Booking readiness</div>
                <div className="text-sm text-[#78350F] leading-snug">Book <b>7–10 days</b> before your cargo-ready date to secure the best sailing and pickup window.</div>
                <div className="mt-2">
                  <Label className="data-label text-[#92400E]">Cargo ready date</Label>
                  <Input data-testid="fob-ready" type="date" value={readyDate} onChange={(e) => setReadyDate(e.target.value)} className="rounded-none mt-1.5 h-9 metric text-sm bg-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-zinc-50 border border-zinc-200 p-4 space-y-2">
            <div className="data-label flex items-center gap-1.5"><Calculator className="w-3 h-3 text-[#002FA7]" />Quick summary</div>
            <SummaryRow label="Shipment" value={SHIPMENT_TYPES.find((s) => s.id === shipmentType)?.label} />
            <SummaryRow label="Container" value={shipmentType === "FCL" ? containerType : "—"} />
            <SummaryRow label="CBM" value={`${calc.cbm.toFixed(2)} m³`} />
            <SummaryRow label="Weight" value={`${calc.kg.toFixed(0)} kg`} />
            <SummaryRow label="Modes" value={Object.entries(modes).filter(([, v]) => v).map(([k]) => k).join(", ") || "—"} />
            <SummaryRow label="Add-ons" value={Object.entries(addOns).filter(([, v]) => v).length || "None"} />
          </div>

          <div className="text-xs text-zinc-500 space-y-2">
            <div className="data-label">Why WeDelvr?</div>
            <ul className="space-y-1.5">
              <li className="flex gap-2"><Shield className="w-3 h-3 text-[#002FA7] mt-0.5 flex-shrink-0" /><span>Customer-specific FAK contract rates pre-negotiated for your volume</span></li>
              <li className="flex gap-2"><FileCheck className="w-3 h-3 text-[#002FA7] mt-0.5 flex-shrink-0" /><span>End-to-end visibility from cargo-ready to delivery</span></li>
              <li className="flex gap-2"><Star className="w-3 h-3 text-[#002FA7] mt-0.5 flex-shrink-0" /><span>Carrier scorecards based on real on-time performance</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* RESULTS */}
      {hasSearched && !busy && (
        <div className="bg-white border border-zinc-200">
          <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-heading text-lg font-bold tracking-tight">{filtered.length} freight option{filtered.length !== 1 ? "s" : ""}</h3>
              <p className="text-xs text-zinc-500 metric">Sort and filter to find the best fit.</p>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <span className="data-label">Sort by</span>
              <select data-testid="fob-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="rounded-none h-9 border border-zinc-200 text-sm px-2 metric">
                {SORT_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <span className="data-label">Carrier</span>
              <select data-testid="fob-carrier-filter" value={carrierFilter} onChange={(e) => setCarrierFilter(e.target.value)} className="rounded-none h-9 border border-zinc-200 text-sm px-2 metric max-w-[160px]">
                <option value="ALL">All carriers</option>
                {carriersInResults.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <AlertTriangle className="w-8 h-8 text-zinc-300 mx-auto mb-3" />
              <p className="text-zinc-700 font-medium">No quotes match your filters.</p>
              <p className="text-xs text-zinc-500 mt-1">Try different origin/destination ports or fewer add-ons.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr className="text-left">{["Carrier", "Route", "Mode", "Transit", "Reviews", "Sell rate", "Add-ons", "All-in total", ""].map((h) => <th key={h} className="px-4 py-2.5 data-label">{h}</th>)}</tr>
                </thead>
                <tbody data-testid="fob-results-tbody">
                  {filtered.map((q, i) => (
                    <tr key={q.id || i} className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`fob-result-${i}`}>
                      <td className="px-4 py-3"><div className="font-heading font-semibold">{q.carrier}</div><div className="metric text-[10px] text-zinc-400">{q.forwarder || ""}</div></td>
                      <td className="px-4 py-3 metric text-xs">{q.origin_port} → {q.destination_port}</td>
                      <td className="px-4 py-3 text-xs">{q.mode?.replace("_", " ")}</td>
                      <td className="px-4 py-3 metric text-xs"><Clock className="w-3 h-3 inline mr-1 text-zinc-400" />{q.transit_days}d</td>
                      <td className="px-4 py-3"><div className="flex items-center gap-1"><Star className="w-3 h-3 text-[#F59E0B] fill-[#F59E0B]" /><span className="text-xs font-medium">{q.reviews_rating}</span><span className="metric text-[10px] text-zinc-400">({q.reviews_count})</span></div></td>
                      <td className="px-4 py-3 metric text-xs">{usd(q.total || 0)}</td>
                      <td className="px-4 py-3 metric text-xs text-zinc-500">{q.add_ons_total ? `+${usd(q.add_ons_total)}` : "—"}</td>
                      <td className="px-4 py-3 metric font-semibold">{usd(q.grand_total || 0)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button data-testid={`fob-book-${i}`} size="sm" className="rounded-none h-8 bg-[#002FA7] hover:bg-[#002480] text-xs" onClick={() => navigate("/bookings")}>Book <ChevronRight className="w-3 h-3 ml-1" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!hasSearched && (
        <div className="bg-white border border-zinc-200 p-10 text-center" data-testid="fob-empty">
          <Search className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-700 font-medium">Ready to compare rates</p>
          <p className="text-xs text-zinc-500 mt-1">Fill in shipment details above and click <span className="font-semibold text-zinc-700">Compare rates</span>.</p>
        </div>
      )}
    </div>
  );
}

/* -------------------- Sub-components -------------------- */
function Section({ number, title, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-2">
        <span className="inline-flex items-center justify-center w-5 h-5 bg-[#002FA7] text-white text-[10px] font-bold metric">{number}</span>
        <h3 className="font-heading font-bold tracking-tight">{title}</h3>
        <span className="text-[11px] text-zinc-500 metric">{hint}</span>
      </div>
      {children}
    </div>
  );
}

function LocationCard({ label, data, onChange, testIdPrefix }) {
  return (
    <div className="border border-zinc-200 p-3 bg-zinc-50/40">
      <div className="data-label mb-2">{label}</div>
      <div className="space-y-2">
        <Input data-testid={`${testIdPrefix}-city`} value={data.city} onChange={(e) => onChange({ ...data, city: e.target.value })} placeholder="City, e.g. Shanghai" className="rounded-none h-9 text-sm metric" />
        <Input data-testid={`${testIdPrefix}-country`} value={data.country} onChange={(e) => onChange({ ...data, country: e.target.value })} placeholder="Country" className="rounded-none h-9 text-sm metric" />
        <Combobox
          testId={`${testIdPrefix}-type`}
          value={data.type}
          onChange={(v) => onChange({ ...data, type: v })}
          options={LOCATION_TYPES.map((l) => ({ value: l.id, label: l.label }))}
          placeholder="Location type"
        />
        {(data.type === "RESIDENTIAL" || data.type === "BUSINESS") && (
          <label className="flex items-center gap-2 text-xs metric text-zinc-600" data-testid={`${testIdPrefix}-liftgate`}>
            <input type="checkbox" checked={data.liftGate} onChange={(e) => onChange({ ...data, liftGate: e.target.checked })} />
            Lift-gate required
          </label>
        )}
      </div>
    </div>
  );
}

function CalcStat({ label, value, sub }) {
  return (
    <div className="bg-white p-3">
      <div className="data-label">{label}</div>
      <div className="metric text-lg font-medium mt-1">{value}</div>
      {sub && <div className="metric text-[10px] text-zinc-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-zinc-500 metric">{label}</span>
      <span className="font-medium text-right">{value || "—"}</span>
    </div>
  );
}
